/**
 * Average Directional Index (ADX)
 *
 * Measures trend strength regardless of direction (0–100 scale).
 * Also plots +DI (bullish directional movement) and −DI (bearish).
 * ADX > 25 is generally considered a strong trend.
 * Default period: 14.
 *
 * Category: Trend | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const adxIndicator: IndicatorDefinition = {
  id: "adx",
  name: "Average Directional Index",
  shortName: "ADX",
  category: "trend",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 1, max: 200 },
  ],
  plots: [
    { key: "adx", label: "ADX", color: "#FF9800", style: "line", lineWidth: 2, priceScaleId: "adx" },
    { key: "plusDI", label: "+DI", color: "#4CAF50", style: "line", lineWidth: 1, priceScaleId: "adx" },
    { key: "minusDI", label: "-DI", color: "#F44336", style: "line", lineWidth: 1, priceScaleId: "adx" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    if (candles.length < period + 1) {
      return [
        { key: "adx", data: [] },
        { key: "plusDI", data: [] },
        { key: "minusDI", data: [] },
      ];
    }

    const trueRange: number[] = [];
    const plusDM: number[] = [];
    const minusDM: number[] = [];

    for (let i = 1; i < candles.length; i++) {
      const high = candles[i].high;
      const low = candles[i].low;
      const prevClose = candles[i - 1].close;
      const prevHigh = candles[i - 1].high;
      const prevLow = candles[i - 1].low;

      // True Range
      trueRange.push(Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose)));

      // Directional Movement
      const upMove = high - prevHigh;
      const downMove = prevLow - low;
      plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0);
      minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0);
    }

    // Smoothed averages using Wilder's method
    const smooth = (arr: number[], p: number): number[] => {
      const result: number[] = [];
      let sum = 0;
      for (let i = 0; i < arr.length; i++) {
        if (i < p) {
          sum += arr[i];
          if (i === p - 1) result.push(sum);
          else result.push(NaN);
        } else {
          const prev = result[result.length - 1];
          result.push(prev - prev / p + arr[i]);
        }
      }
      return result;
    };

    const smoothTR = smooth(trueRange, period);
    const smoothPlusDM = smooth(plusDM, period);
    const smoothMinusDM = smooth(minusDM, period);

    const adxData: { time: number; value: number }[] = [];
    const plusDIData: { time: number; value: number }[] = [];
    const minusDIData: { time: number; value: number }[] = [];

    const dx: number[] = [];
    for (let i = 0; i < smoothTR.length; i++) {
      if (isNaN(smoothTR[i]) || smoothTR[i] === 0) {
        dx.push(NaN);
        continue;
      }
      const pdi = (smoothPlusDM[i] / smoothTR[i]) * 100;
      const mdi = (smoothMinusDM[i] / smoothTR[i]) * 100;

      plusDIData.push({ time: candles[i + 1].time, value: pdi });
      minusDIData.push({ time: candles[i + 1].time, value: mdi });

      const diSum = pdi + mdi;
      dx.push(diSum === 0 ? 0 : (Math.abs(pdi - mdi) / diSum) * 100);
    }

    // ADX = smoothed DX
    const validDx = dx.filter(v => !isNaN(v));
    const smoothedADX = smooth(validDx, period);

    let adxStartIdx = 0;
    for (let i = 0; i < dx.length; i++) {
      if (!isNaN(dx[i])) {
        if (adxStartIdx < smoothedADX.length && !isNaN(smoothedADX[adxStartIdx])) {
          adxData.push({ time: candles[i + 1].time, value: smoothedADX[adxStartIdx] });
        }
        adxStartIdx++;
      }
    }

    return [
      { key: "adx", data: adxData },
      { key: "plusDI", data: plusDIData },
      { key: "minusDI", data: minusDIData },
    ];
  },
};
